<template>
  <div
    :id="dropdownId"
    v-tooltip="{
      content: selectedValue.label,
      onShow: () => showTooltip && isDisplayValueOverflowing,
    }"
    class="relative h-10 min-w-56 rounded-sm border bg-white px-4 text-sm"
    :class="
      showRootMenu
        ? 'border-2 border-primary hover:border-primary'
        : 'border hover:border-gray-300'
    "
    @click="toggleDropdown"
  >
    <span
      v-if="placeholder && selectedValue.id"
      class="pointer-events-none absolute -top-3 left-4 bg-white px-0.5 text-xs select-none"
    >
      {{ placeholder }}
    </span>

    <div
      class="pointer-events-none flex h-full w-full items-center justify-between text-gray-600"
    >
      <div class="overflow-hidden text-ellipsis whitespace-nowrap">
        <slot name="selected" :value="selectedValue">
          <span>
            {{ selectedValue.label || placeholder }}
          </span>
        </slot>
      </div>

      <icon-chevron-up
        class="transform transition-all"
        :class="{ 'rotate-180': showRootMenu }"
        :fill="arrowColor"
      />
    </div>

    <div
      v-if="showRootMenu && optionSources.length"
      v-on-click-outside="onDropdownClickOutside"
      ref="topMenuEl"
      class="rounded-md border bg-white shadow-md"
      :id="COMPOSITE_DROPDOWN_MODAL_ID"
      :class="dropdownClassNames"
    >
      <ul class="flex max-w-80 min-w-80 flex-col space-y-4 px-2 select-none">
        <div class="sticky top-0 z-10 w-full bg-white px-2 pt-4">
          <search-input
            v-model="searchTerm"
            :search-term="placeholder"
            no-padding
            no-margin
          />
        </div>

        <lf-composite-dropdown-source
          v-for="(source, sourceIndex) in optionSources"
          :key="source.id"
          :class="{ 'pb-4': sourceIndex === optionSources.length - 1 }"
          :search-term="searchTerm"
          :source="source"
          :selected-value="selectedValue"
          @update:selected-value="selectValue"
        >
          <template #option="{ key, option }">
            <slot name="option" :option="option" :key="key"></slot>
          </template>
        </lf-composite-dropdown-source>
      </ul>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    TData extends { id: string | number },
    TResponse extends {
      data: TData[];
      meta: { current_page: number; last_page: number };
    },
    TSourceCallback extends (params?: any) => Promise<TResponse>,
    TSourceObject extends Record<string, string>
  "
>
import { computed, ref, useTemplateRef } from "vue";

import { v4 as uuidv4 } from "uuid";

import { COMPOSITE_DROPDOWN_MODAL_ID } from "@/helpers/constants/deals";
import { useTeleportV2 } from "@/hooks/elements";

import IconChevronUp from "@/components/icons/deals/IconChevronUp.vue";
import LfCompositeDropdownSource from "@/components/ui/inputs/LfCompositeDropdownSource.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

type TSource = {
  id: string;
  title: string;
  options: TSourceCallback | TSourceObject;
  propertyToRender?: string;
};
type TModelValue = { id: string | number; label: string; source: string };

const {
  modelValue,
  placeholder = "",
  dropdownClassNames = "z-10",
} = defineProps<{
  /** Required Props */
  modelValue: TModelValue;
  optionSources: TSource[];
  /** Optional Props */
  disabled?: boolean;
  placeholder?: string;
  showTooltip?: boolean;
  dropdownClassNames?: string;
}>();

const emit = defineEmits<{ "update:model-value": [val: TModelValue] }>();
const arrowColor = "#99a1af";

const dropdownId = uuidv4();
const topMenuEl = useTemplateRef("topMenuEl");
const showRootMenu = ref(false);
const searchTerm = ref("");

const selectedValue = computed({
  get() {
    return modelValue;
  },
  set(val) {
    emit("update:model-value", val);
  },
});

const isDisplayValueOverflowing = computed(() => {
  return selectedValue.value.label.length > 30;
});

const onDropdownClickOutside = (event: PointerEvent) => {
  if ((event.target as HTMLElement).id === dropdownId) {
    return;
  }
  resetDropdown();
};

const selectValue = (newValue: TModelValue) => {
  selectedValue.value = newValue;
  resetDropdown();
};

const resetDropdown = () => {
  if (showRootMenu.value) {
    showRootMenu.value = false;
    searchTerm.value = "";
  }
};

const toggleDropdown = () => {
  if (showRootMenu.value) {
    resetDropdown();
  } else {
    showRootMenu.value = true;
  }
};

useTeleportV2({
  target: topMenuEl,
  to: "#menus",
  offset: { top: 5 },
  checkForOverflow: true,
});
</script>
